import { useState, useEffect, useCallback } from 'react';
import { getEnergyStats } from '../api/api';
import logger from '../utils/logger';

export const useEnergyMeter = (
  processId,
  fullVersion = '0.0.0',
  processVersion = '0.x.x',
  level = null,
  isHistorical = false
) => {
  const [active, setActive] = useState(isHistorical);
  const [mode, setMode] = useState(isHistorical ? 'analysis' : 'input');
  const [stats, setStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [promptActivity, setPromptActivity] = useState(null); // { id, title, position }
  const [popoverActivity, setPopoverActivity] = useState(null); // { id, title, position }

  useEffect(() => {
    if (isHistorical) {
      setActive(true);
      setMode('analysis');
    }
  }, [isHistorical]);

  const fetchStats = useCallback(async () => {
    if (!processId) return;
    const res = await getEnergyStats(processId, fullVersion, level);
    if (res) {
      setStats(res);
      setLastUpdated(Date.now());
    }
  }, [processId, fullVersion, level]);

  useEffect(() => {
    if (active) {
      fetchStats();
    }
  }, [active, fetchStats]);

  const toggleEnergyMeter = () => {
    setActive((prev) => {
      const next = !prev;
      if (!next) {
        setPromptActivity(null);
        setPopoverActivity(null);
      }
      return next;
    });
  };

  const openPrompt = (activity) => {
    if (isHistorical) return;
    setPopoverActivity(null);
    setPromptActivity((prev) => {
      const isSame = prev && String(prev.id) === String(activity.id);
      return isSame ? null : activity;
    });
  };

  const openPopover = (activity) => {
    logger.info('openPopover: called with', activity);
    const actId = activity?.id;

    // Check if the threshold is passed for this activity
    const activityStats =
      stats?.activities?.[actId] ||
      stats?.activities?.[String(actId)] ||
      stats?.activities?.[Number(actId)];

    if (!activityStats || !activityStats.threshold_passed) {
      logger.info('openPopover: threshold not passed or no stats, ignoring click');
      return;
    }

    setPromptActivity(null);
    setPopoverActivity((prev) => {
      const isSame = prev && String(prev.id) === String(actId);
      logger.info('openPopover: toggling popover. isSame =', isSame);
      return isSame ? null : activity;
    });
  };

  const closeOverlays = () => {
    setPromptActivity(null);
    setPopoverActivity(null);
  };

  return {
    active,
    mode: isHistorical ? 'analysis' : mode,
    setMode: isHistorical ? () => {} : setMode,
    stats,
    lastUpdated,
    hotspotCount: stats?.hotspot_count || 0,
    totalRatings: stats?.grand_total_ratings || 0,
    majorVersion: stats?.process_major ?? 0,
    processVersion,
    promptActivity,
    popoverActivity,
    toggleEnergyMeter,
    openPrompt: isHistorical ? () => {} : openPrompt,
    openPopover,
    closeOverlays,
    refreshStats: fetchStats,
    isHistorical,
  };
};

export default useEnergyMeter;
